import { graphql, PageProps } from 'gatsby';
import { GatsbyImage, getImage } from 'gatsby-plugin-image';
import { useTranslation } from 'gatsby-plugin-react-i18next';
import parse from 'html-react-parser';

import { BackNavigation } from '@/components/BackNavigation';
import { Layout } from '@/components/Layout';
import { Gallery, GalleryLayout, GalleryProps } from '@/components/Gallery';
import { Seo } from '@/components/Seo';
import { VideoSection } from '@/components/VideoSection';

const COLOR_SCHEME = 'color-scheme4';

const galleryOptions: GalleryProps['options'] = {
  initialIndex: 2,
  wrapAround: true,
  pageDots: false,
  adaptiveHeight: false,
};

const ArticlePage = ({ data }: PageProps<Queries.ArticlePageQuery>) => {
  const { t } = useTranslation();

  if (!data.article) {
    return null;
  }

  const imageData =
    data.article.featured_image?.localFile &&
    getImage(data.article.featured_image.localFile.childImageSharp);

  return (
    <Layout data={data}>
      <Seo
        title={''}
        bodyAttributes={{
          class: COLOR_SCHEME,
        }}
      />

      <div className='template-article'>
        <BackNavigation title={t('NEWS')} />
        <div className='content'>
          <div className='grid'>
            {imageData && (
              <GatsbyImage alt={''} className='main-image' image={imageData} />
            )}

            {data.article.body?.data && (
              <div className='article-content'>
                <h2>{data.article.title}</h2>
                <section>
                  {data.article.body?.data &&
                    parse(data.article.body.data.body)}
                </section>
              </div>
            )}
          </div>

          {data.article.images && (
            <Gallery
              title={new Intl.DateTimeFormat('it-IT').format(
                new Date(data.article.createdAt!)
              )}
              data={data.article.images.map((image) => ({
                image: image!.localFile!.publicURL!,
                title: '',
                description: '',
                link: '',
              }))}
              layout={GalleryLayout.B}
              options={galleryOptions}
            />
          )}

          {data.article.sections?.map((section) => {
            if (!section) {
              return null;
            }

            switch (section.strapi_component) {
              case 'general.video-link':
                return (
                  <VideoSection
                    section={section as Queries.VideoSectionFragmentFragment}
                  />
                );
              default:
                return null;
            }
          })}
        </div>
      </div>
    </Layout>
  );
};

export default ArticlePage;

export const query = graphql`
  query ArticlePage($language: String!, $slug: String!) {
    ...Layout

    article: strapiArticle(slug: { eq: $slug }) {
      createdAt
      body {
        data {
          body
        }
      }
      featured_image {
        alternativeText
        localFile {
          childImageSharp {
            gatsbyImageData(layout: FULL_WIDTH)
          }
        }
      }
      slug
      title
      images {
        alternativeText
        localFile {
          publicURL
        }
      }
      sections {
        ...VideoSectionFragment
      }
    }
  }
`;
